import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  createSupabaseRouteCheckpointSource,
  type MobileRouteCheckpointCandidate,
  type SupabaseRouteCheckpointClient,
} from "../../../../packages/guard-core/src/route-checkpoint-source.ts";
import type { CheckpointOption } from "./CaptureScreen";

// Route checkpoint list wiring for the manual picker. Uses guard-core's account-scoped route
// checkpoint source (explicit token-safe selects — no wildcard, no token/hash fields) — the same
// path the native guard app uses, so a guard session can read it. Manual fallback is secondary to
// QR, so a load error degrades to an empty list (the screen stays honestly gated) rather than
// blocking capture.

export interface GuardCheckpoints {
  routeAssignmentId: string | null;
  checkpoints: CheckpointOption[];
}

const EMPTY: GuardCheckpoints = { routeAssignmentId: null, checkpoints: [] };

/** Map route checkpoint candidates to manual-picker options. Only checkpoints whose config allows a
 *  manual fallback are offered, so a guard can't manually capture a QR/NFC-only point. */
export function mapCheckpointOptions(
  candidates: readonly MobileRouteCheckpointCandidate[],
): CheckpointOption[] {
  return candidates
    .filter((candidate) => candidate.manualFallbackReady)
    .map((candidate) => ({ checkpointId: candidate.checkpointId, label: candidate.name }));
}

/**
 * Load the signed-in guard's current route checkpoints for the manual picker. Returns an empty list
 * when the guard has no active/scheduled assignment. Throws on a read error (the hook turns that into
 * an honest empty/error state).
 */
export async function loadGuardCheckpoints(
  source: Pick<
    ReturnType<typeof createSupabaseRouteCheckpointSource>,
    "loadLatestAssignmentForGuard" | "loadCheckpointCandidates"
  >,
  guardUserId: string,
): Promise<GuardCheckpoints> {
  const assignment = await source.loadLatestAssignmentForGuard(guardUserId);
  if (!assignment) {
    return EMPTY;
  }
  const candidates = await source.loadCheckpointCandidates(assignment.routeId);
  return { routeAssignmentId: assignment.id, checkpoints: mapCheckpointOptions(candidates) };
}

export type GuardCheckpointsStatus = "loading" | "ready" | "error";

export interface UseGuardCheckpointsResult {
  status: GuardCheckpointsStatus;
  data: GuardCheckpoints;
  error: string | null;
}

/** Load the guard's route checkpoints on mount. Errors degrade to an empty list (manual fallback is
 *  secondary; QR stays primary), with an error flag for an optional notice. */
export function useGuardCheckpoints(
  client: SupabaseClient,
  accountId: string,
  guardUserId: string,
): UseGuardCheckpointsResult {
  const [status, setStatus] = useState<GuardCheckpointsStatus>("loading");
  const [data, setData] = useState<GuardCheckpoints>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    // The real supabase-js builder is thenable + chainable at runtime (the same path the native app
    // uses); the cast bridges its nominal PostgrestQueryBuilder type to guard-core's structural one.
    const source = createSupabaseRouteCheckpointSource(
      client as unknown as SupabaseRouteCheckpointClient,
      { activeAccountId: accountId },
    );
    loadGuardCheckpoints(source, guardUserId)
      .then((result) => {
        if (!active) return;
        setData(result);
        setError(null);
        setStatus("ready");
      })
      .catch(() => {
        if (!active) return;
        setData(EMPTY);
        setError("Couldn't load your route checkpoints.");
        setStatus("error");
      });
    return () => {
      active = false;
    };
  }, [client, accountId, guardUserId]);

  return { status, data, error };
}
